import { pick } from '../../i18n.js';

/** Home page and project library copy in each supported language. */
export const text = pick({
  zh: {
    heading: 'AiMo 可以如何帮你？',
    intent: '设计意图',
    taskType: '任务类型',
    start: '开始设计',
    startHint: '开始设计（Enter）',
    recent: '最近项目',
    starters: '从示例开始',
    newProject: '新建项目',
    newProjectButton: '＋ 新建项目',
    projectName: '项目名称',
    untitled: (n: number) => `未命名项目 ${n}`,
    blankTitle: '空白画布',
    blankDescription: '从空白画布开始，上传资料或连接 Rhino 后再描述任务。',
    account: (name: string) => `当前账号 ${name}`,
    logout: '退出',
    login: '登录',
    library: '项目库',
    openLibrary: '打开项目库',
    closeLibrary: '关闭项目库',
    expandLibrary: '展开项目库',
    collapseLibrary: '收起项目库',
    projectCount: ' 个项目',
    search: '搜索项目',
    filters: '项目筛选',
    filterRecent: '最近',
    filterFavorites: '收藏',
    filterAll: '全部',
    myProjects: '我的项目',
    noMatches: '暂无匹配项目',
    signInToSee: '登录后查看你的项目',
    emptyHint: '输入设计意图即可创建新项目',
    updated: (when: string) => `更新于 ${when}`,
    enterCanvas: '进入画布',
    favorite: '收藏',
    unfavorite: '取消收藏',
    moreActions: (name: string) => `${name} 的更多操作`,
    openProject: '打开项目',
    libraries: '经验库',
    librariesHint: '任务完成后自动整理的经验；新任务会自动参考。',
    sharedLibrary: '共享经验库',
    entryCount: (n: number) => `${n} 条经验`,
    libraryOff: '已停用',
    browseLibrary: (name: string) => `浏览经验库 ${name}`,
    browseLibraries: '浏览经验库',
    libraryList: '经验库列表',
    searchEntries: '搜索经验',
    noEntries: '这个库还没有经验条目',
    loadFailed: '经验库暂时不可读取。',
    enableLibrary: '在新任务中自动参考并写入',
    autoCaptureOn: '自动捕获已开启',
    autoCaptureOff: '自动捕获未启用（没有配置快速模型），仍可从任务卡写入经验',
    showArchived: '显示已归档',
    entries: '经验条目',
    back: '返回列表',
    detail: '详情',
    facts: '要点',
    sources: '来源',
    sourceRun: (runId: string, trigger: string) => `任务 ${runId.slice(0, 8)} · ${trigger === 'manual' ? '手工写入' : '任务完成时自动捕获'}`,
    artifacts: (n: number) => `${n} 个产物`,
    versions: '版本',
    recalledIn: '被这些任务参考',
    neverRecalled: '还没有任务参考过',
    archiveEntry: '归档',
    restoreEntry: '恢复',
    supersededBy: '已被更新的条目取代',
    updatedAt: (when: string) => `更新于 ${when}`,
  },
  en: {
    heading: 'How can AiMo help you?',
    intent: 'Design intent',
    taskType: 'Task type',
    start: 'Start designing',
    startHint: 'Start designing (Enter)',
    recent: 'Recent projects',
    starters: 'Start from an example',
    newProject: 'New project',
    newProjectButton: '＋ New project',
    projectName: 'Project name',
    untitled: (n: number) => `Untitled project ${n}`,
    blankTitle: 'Blank canvas',
    blankDescription: 'Start from a blank canvas; upload material or connect Rhino, then describe the task.',
    account: (name: string) => `Signed in as ${name}`,
    logout: 'Sign out',
    login: 'Sign in',
    library: 'Project library',
    openLibrary: 'Open project library',
    closeLibrary: 'Close project library',
    expandLibrary: 'Expand project library',
    collapseLibrary: 'Collapse project library',
    projectCount: ' projects',
    search: 'Search projects',
    filters: 'Project filters',
    filterRecent: 'Recent',
    filterFavorites: 'Starred',
    filterAll: 'All',
    myProjects: 'My projects',
    noMatches: 'No matching projects',
    signInToSee: 'Sign in to see your projects',
    emptyHint: 'Describe a design intent to create a project',
    updated: (when: string) => `Updated ${when}`,
    enterCanvas: 'Open canvas',
    favorite: 'Star',
    unfavorite: 'Unstar',
    moreActions: (name: string) => `More actions for ${name}`,
    openProject: 'Open project',
    libraries: 'Libraries',
    librariesHint: 'Experience distilled automatically when tasks finish; new tasks recall it on their own.',
    sharedLibrary: 'Shared library',
    entryCount: (n: number) => `${n} ${n === 1 ? 'entry' : 'entries'}`,
    libraryOff: 'Off',
    browseLibrary: (name: string) => `Browse library ${name}`,
    browseLibraries: 'Browse libraries',
    libraryList: 'Libraries',
    searchEntries: 'Search entries',
    noEntries: 'This library has no entries yet',
    loadFailed: 'Libraries cannot be read right now.',
    enableLibrary: 'Recall and capture in new tasks',
    autoCaptureOn: 'Automatic capture is on',
    autoCaptureOff: 'Automatic capture is not configured (no fast model); entries can still be written from a task card',
    showArchived: 'Show archived',
    entries: 'Entries',
    back: 'Back to the list',
    detail: 'Detail',
    facts: 'Facts',
    sources: 'Sources',
    sourceRun: (runId: string, trigger: string) => `Task ${runId.slice(0, 8)} · ${trigger === 'manual' ? 'written manually' : 'captured when the task finished'}`,
    artifacts: (n: number) => `${n} ${n === 1 ? 'artifact' : 'artifacts'}`,
    versions: 'Versions',
    recalledIn: 'Recalled by these tasks',
    neverRecalled: 'Not recalled by any task yet',
    archiveEntry: 'Archive',
    restoreEntry: 'Restore',
    supersededBy: 'Superseded by a newer entry',
    updatedAt: (when: string) => `Updated ${when}`,
  },
});
